/**
 * Epic 2 — sestavení "merge apply bundle" (granulární výběr → schema+canvas
 * commandy → sloučený stav), sdílené mezi `SyncPanel.tsx` (E2-US4) a Epic 9's
 * `ChangeScriptPanel.tsx` (pre-flight krok, epic-9 kap. 2 — "nekonfliktní DB
 * drift se řeší standardním flow synchronizace"). Vytaženo ze `SyncPanel.tsx`
 * beze změny chování, jen aby ho nemusel duplikovat druhý konzument.
 */
import type { CanvasMetadata, SchemaModel, SyncDiffItem, TableModel } from '@bits2schema/shared';
import { resolveForeignKeyEdges } from '@bits2schema/shared';
import { buildSyncMergePlan, type SyncMergePlan } from '../schema/syncMerge';
import {
  createClearTableCanvasStateCommand,
  createDeleteNoteCommand,
  createPlaceTableCommand,
  createRemoveTableFromGroupCommand,
  type Command as CanvasCommand,
} from './commands';
import type { BoxSize } from './geometry';
import { computeIncrementalPlacement, type Point } from './layout';

export interface ApplyBundle {
  plan: SyncMergePlan;
  canvasCommands: CanvasCommand[];
  mergedCanvas: CanvasMetadata;
}

export function connectedTableIds(schema: SchemaModel, tableId: string): string[] {
  const ids = new Set<string>();
  for (const edge of resolveForeignKeyEdges(schema)) {
    if (edge.sourceTableId === tableId && edge.targetTableId) ids.add(edge.targetTableId);
    else if (edge.targetTableId === tableId) ids.add(edge.sourceTableId);
  }
  return [...ids];
}

export function buildApplyBundle(
  schema: SchemaModel,
  canvas: CanvasMetadata,
  rescannedSchema: SchemaModel,
  diff: SyncDiffItem[],
  effectiveTablePositions: Record<string, Point>,
  tableSizes: Record<string, BoxSize>,
): ApplyBundle {
  const plan = buildSyncMergePlan(schema, rescannedSchema, diff);
  const canvasCommands: CanvasCommand[] = [];

  // E2-US4 AC — tabulka mizející z modelu si odnese i svou canvas stopu
  // (pozici, barvu, členství ve skupině, připnuté poznámky), stejný úklid
  // jako `DeleteTableDialog.tsx` (Epic 4) pro jednu tabulku ručně.
  for (const table of plan.removedTables) {
    const notes = canvas.notes.filter((note) => {
      const attachment = note.attachment;
      if (attachment.type === 'table') return attachment.id === table.id;
      if (attachment.type === 'column') return table.columns.some((column) => column.id === attachment.id);
      return false;
    });
    for (const note of notes) {
      canvasCommands.push(createDeleteNoteCommand(note, canvas.notes.findIndex((candidate) => candidate.id === note.id)));
    }
    const group = canvas.groups.find((candidate) => candidate.memberTableIds.includes(table.id));
    if (group) {
      canvasCommands.push(createRemoveTableFromGroupCommand(group.id, table.id, group.memberTableIds.indexOf(table.id)));
    }
    canvasCommands.push(
      createClearTableCanvasStateCommand(table.id, canvas.tablePositions[table.id], canvas.tableColors[table.id] ?? null),
    );
  }

  // Sloupec smazaný z JINAK zachované tabulky (`DeleteColumnDialog.tsx` dělá totéž ručně).
  for (const column of plan.deletedColumns) {
    const notes = canvas.notes.filter((note) => note.attachment.type === 'column' && note.attachment.id === column.id);
    for (const note of notes) {
      canvasCommands.push(createDeleteNoteCommand(note, canvas.notes.findIndex((candidate) => candidate.id === note.id)));
    }
  }

  // E2-US4 AC3 / Epic 7 E7-US3 — nové tabulky z DB se umístí inkrementálním
  // algoritmem, ne na jednu hromadu; pozice se počítají postupně, aby dvě
  // nové tabulky vzniklé ze stejného mergu nepřekryly jedna druhou.
  const positions = { ...effectiveTablePositions };
  for (const table of plan.addedTables) {
    const position = computeIncrementalPlacement(
      plan.mergedSchema,
      positions,
      tableSizes,
      table.id,
      connectedTableIds(plan.mergedSchema, table.id),
    );
    positions[table.id] = position;
    canvasCommands.push(createPlaceTableCommand(table.id, undefined, position));
  }

  const mergedCanvas = canvasCommands.reduce((acc, command) => command.apply(acc), canvas);
  return { plan, canvasCommands, mergedCanvas };
}

export interface RemovalWarning {
  table: TableModel;
  groupName: string | null;
  noteCount: number;
}

export function collectRemovalWarnings(canvas: CanvasMetadata, removedTables: TableModel[]): RemovalWarning[] {
  const warnings: RemovalWarning[] = [];
  for (const table of removedTables) {
    const group = canvas.groups.find((candidate) => candidate.memberTableIds.includes(table.id));
    const noteCount = canvas.notes.filter((note) => {
      const attachment = note.attachment;
      if (attachment.type === 'table') return attachment.id === table.id;
      if (attachment.type === 'column') return table.columns.some((column) => column.id === attachment.id);
      return false;
    }).length;
    if (group || noteCount > 0) warnings.push({ table, groupName: group?.name ?? null, noteCount });
  }
  return warnings;
}
